import { Node as ProseMirrorNode } from "prosemirror-model";
import { Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import { capitalizeFirstLetter } from "~/lib/utils";

import { Extension } from "../extension";
import { isCollapsed } from "./collapse";

const GOAL_LABEL = window.APP_CONFIG && window.APP_CONFIG.priorityLabel ? window.APP_CONFIG.priorityLabel : "Goal";

const getNodeTypeDisplayName = (typeName: string): string => {
	if (typeName.startsWith("header")) return "Section Heading";
	switch (typeName) {
		case "agenda":
			return "Talking Point";
		case "todo":
			return "Action Item";
		case "goal":
			return GOAL_LABEL;
		default:
			return capitalizeFirstLetter(typeName);
	}
};

/**
 * Some templates, goals in particular, include replacement codes for account-specific text.
 *
 */
function formatPlaceholderReplacements(text: string): string {
	return text.replace("{priority_label}", GOAL_LABEL.toLowerCase());
}

export class Placeholder extends Extension {
	get name() {
		return "placeholder";
	}

	get defaultOptions() {
		return {
			placeholderClass: "prose-placeholder",
			placeholderText: "Type “/” to open items list",
		};
	}

	static defaultPlaceholderText = ({ type }: ProseMirrorNode) => {
		return `New ${getNodeTypeDisplayName(type.name).toLowerCase()}`;
	};

	get plugins() {
		return [
			new Plugin({
				props: {
					decorations: state => {
						const decorations: Decoration[] = [];
						const { selection } = state;

						const decorate = (node: ProseMirrorNode, pos: number) => {
							const isEmpty =
								node.childCount === 0 ||
								(isCollapsed(state, node) &&
									node.type.name === "header-one" &&
									node.firstChild?.type.name === "hard_break");
							if (!node.type.isBlock || !node.attrs.blockId || !isEmpty) return;
							// Two types of placeholders:
							// 1. blocks that has a placeholder attr:  shown regardless of selection state
							//     a. if placeholder is not empty, show that placeholder
							//     b. if placeholder is empty, show BlockType based default placeholder
							// 2. Show 'type "/" for commands'. Shown only when selected
							if (
								typeof node.attrs.placeholder === "undefined" &&
								(state.selection.$anchor.parent !== node || !state.selection.empty)
							)
								return false;
							const text =
								typeof node.attrs.placeholder !== "undefined"
									? formatPlaceholderReplacements(node.attrs.placeholder) || Placeholder.defaultPlaceholderText(node)
									: this.options.placeholderText;
							if (
								pos + 1 >= selection.from &&
								pos + 1 <= selection.to &&
								node.type.name !== "unstyled" &&
								!node.attrs.originalBlockId &&
								!this.options.showPlaceholderWhenSelectionInside
							)
								return false;
							decorations.push(
								Decoration.widget(
									pos + 1,
									() => {
										const placeholder = document.createElement("div");
										placeholder.classList.add(this.options.placeholderClass);
										if (pos + 1 >= selection.from && pos + 1 <= selection.to) {
											placeholder.classList.add("cursor-visible");
										}
										placeholder.setAttribute("data-placeholder", text);
										return placeholder;
									},
									{
										key: node.attrs.blockId,
										marks: [],
									},
								),
							);
							return false;
						};

						state.doc.descendants(decorate);

						return DecorationSet.create(state.doc, decorations);
					},
				},
			}),
		];
	}
}

export default Placeholder;
